// src/lib/videoFrame.ts

type FrameOpts = {
    /** Seconds into the video to grab. Clamped to (0, duration - 0.05). */
    atSeconds?: number;
    /** Max width of the produced JPEG. Height scales proportionally. */
    maxWidth?: number;
    /** JPEG quality 0..1. */
    quality?: number;
};

const DEFAULTS: Required<FrameOpts> = {
    atSeconds: 0.5,
    maxWidth: 640,
    quality: 0.72,
};

// Session-long caches. Keyed by url + opts so different sizes don't collide.
const frameCache = new Map<string, string>();
const pending = new Map<string, Promise<string | null>>();

function cacheKey(url: string, opts: FrameOpts = {}) {
    const o = { ...DEFAULTS, ...opts };
    return `${url}#${o.atSeconds}#${o.maxWidth}#${o.quality}`;
}

/** Synchronously peek the cache. Useful to avoid a flash on re-mounts. */
export function peekVideoFrame(url: string, opts?: FrameOpts): string | null {
    return frameCache.get(cacheKey(url, opts)) ?? null;
}

/** Drop a cached frame (e.g. after persisting it server-side). */
export function clearVideoFrame(url: string, opts?: FrameOpts): void {
    frameCache.delete(cacheKey(url, opts));
}

/**
 * Extract a single JPEG frame from a video URL using the browser only.
 *
 * Uses a Range request under the hood (S3/MinIO honour `Range` on seek),
 * so only a small chunk around the nearest keyframe is downloaded.
 *
 * Returns a data: URL on success, or null on any failure (CORS taint,
 * codec error, timeout, offline, etc). Never throws.
 */
export async function extractVideoFrame(
    url: string,
    opts?: FrameOpts,
): Promise<string | null> {
    if (typeof document === "undefined" || !url) return null;

    const o = { ...DEFAULTS, ...opts };
    const key = cacheKey(url, o);

    const cached = frameCache.get(key);
    if (cached) return cached;

    const inflight = pending.get(key);
    if (inflight) return inflight;

    const task = (async (): Promise<string | null> => {
        const video = document.createElement("video");
        video.crossOrigin = "anonymous";
        video.muted = true;
        video.playsInline = true;
        video.preload = "metadata";
        video.src = url;

        const waitFor = <K extends keyof HTMLMediaElementEventMap>(
            el: HTMLElement,
            event: K,
            ms: number,
        ) =>
            new Promise<void>((resolve, reject) => {
                const timer = setTimeout(
                    () => reject(new Error(`${String(event)} timeout`)),
                    ms,
                );
                el.addEventListener(
                    event,
                    () => {
                        clearTimeout(timer);
                        resolve();
                    },
                    { once: true },
                );
            });

        try {
            await Promise.race([
                waitFor(video, "loadedmetadata", 8000),
                waitFor(video, "error", 8000).then(() => {
                    throw new Error("metadata load failed");
                }),
            ]);

            const duration = isFinite(video.duration) ? video.duration : o.atSeconds;
            const target = Math.max(
                0.01,
                Math.min(o.atSeconds, Math.max(0, duration - 0.05)),
            );
            video.currentTime = target;

            await Promise.race([
                waitFor(video, "seeked", 8000),
                waitFor(video, "error", 8000).then(() => {
                    throw new Error("seek failed");
                }),
            ]);

            const vw = video.videoWidth || 1280;
            const vh = video.videoHeight || 720;
            const scale = Math.min(1, o.maxWidth / vw);
            const cw = Math.max(1, Math.round(vw * scale));
            const ch = Math.max(1, Math.round(vh * scale));

            const canvas = document.createElement("canvas");
            canvas.width = cw;
            canvas.height = ch;
            const ctx = canvas.getContext("2d");
            if (!ctx) return null;

            ctx.drawImage(video, 0, 0, cw, ch);

            // Will throw SecurityError if the canvas got tainted (missing CORS).
            const dataUrl = canvas.toDataURL("image/jpeg", o.quality);
            frameCache.set(key, dataUrl);
            return dataUrl;
        } catch {
            return null;
        } finally {
            // Free the decoder immediately.
            video.removeAttribute("src");
            try {
                video.load();
            } catch {
                /* noop */
            }
        }
    })();

    pending.set(key, task);
    try {
        return await task;
    } finally {
        pending.delete(key);
    }
}